import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth, db } from '../firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc, getDocs, collection, query, where, deleteDoc } from 'firebase/firestore';
import { logEvent } from '../utils/logEvent';
import { cleanName } from '../utils/names';
import { birthdayError } from '../utils/dates';
import {
  Box, Button, Container, Typography, Paper, TextField,
  Stepper, Step, StepLabel, MenuItem, Divider, Alert, CircularProgress,
  FormControlLabel, Checkbox, Grid, FormControl, InputLabel, Select, FormHelperText,
  Autocomplete,
} from '@mui/material';
import { logError } from '../utils/errorLogger';

const STEPS_DEFAULT = ['Account Type', 'Your Information', 'Athlete Info', 'Waivers'];
const STEPS_RENTER  = ['Account Type', 'Your Information', 'Details', 'Waivers'];
const STEPS_COPARENT = ['Account Type', 'Your Information'];

const experienceLevels = [
  'No experience',
  'Beginner',
  'Intermediate',
  'Advanced',
  'Elite / Collegiate',
];

const beachToSkillGroup = (beachExp) => {
  if (!beachExp) return '';
  const b = beachExp.toLowerCase();
  if (b.includes('no experience') || b.includes('beginner')) return 'Beginner';
  if (b.includes('intermediate')) return 'Intermediate';
  if (b.includes('advanced')) return 'Advanced';
  return '';
};

const beachLevels = [
  'No experience / Beginner',
  'Intermediate',
  'Advanced',
];

const goalOptions = [
  'I want to improve my skills for indoor',
  'I want to improve my skills for beach',
  'I want to train to make my HS team',
  'I want to train to get recruited for college',
];

const adultGoalOptions = [
  'I want to improve my beach volleyball skills',
  'I would like to meet other beach volleyball players',
  'I would like to join a beach volleyball league',
  'I would like to play in some beach volleyball tournaments',
];

const shirtSizes = ['Small', 'Medium', 'Large', 'XL', 'XXL'];

function formatPhone(raw) {
  const digits = raw.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0,3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
}

function isValidPhone(val) {
  return val.replace(/\D/g, '').length === 10;
}

async function lookupZip(zip) {
  if (zip.replace(/\D/g, '').length !== 5) return null;
  try {
    const res = await fetch(`https://api.zippopotam.us/us/${zip}`);
    if (!res.ok) return null;
    const data = await res.json();
    return {
      city: data.places[0]['place name'],
      state: data.places[0]['state abbreviation'],
    };
  } catch { return null; }
}

function validateStep1(form, coParentMode = false) {
  const errors = {};
  if (!form.firstName.trim()) errors.firstName = 'Required';
  if (!form.lastName.trim()) errors.lastName = 'Required';
  if (!form.phone.trim()) errors.phone = 'Required';
  else if (!isValidPhone(form.phone)) errors.phone = 'Enter a valid 10-digit phone number';
  if (!form.address.trim()) errors.address = 'Required';
  if (!form.city.trim()) errors.city = 'Required';
  if (!form.state.trim()) errors.state = 'Required';
  if (!form.zip.trim()) errors.zip = 'Required';
  else if (form.zip.replace(/\D/g, '').length !== 5) errors.zip = 'Enter a valid 5-digit zip code';
  if (form.accountType === 'junior' && !form.relationship) errors.relationship = 'Required';
  // Co-parents inherit emergency contact info from the existing family; don't re-collect.
  if (!coParentMode) {
    if (!form.emergencyContactName.trim()) errors.emergencyContactName = 'Required';
    if (!form.emergencyContactPhone.trim()) errors.emergencyContactPhone = 'Required';
    else if (!isValidPhone(form.emergencyContactPhone)) errors.emergencyContactPhone = 'Enter a valid 10-digit phone number';
    if (!form.emergencyContactRelationship) errors.emergencyContactRelationship = 'Required';
  }
  return errors;
}

function validateStep2(form, additionalAthletes = []) {
  const errors = {};
  // Siblings added on this step were never validated at all — their birthday
  // went straight to Firestore. Keyed by index so the right row lights up.
  additionalAthletes.forEach((extra, i) => {
    if (!extra?.birthday) return;
    const bad = birthdayError(extra.birthday, { athleteType: 'junior' });
    if (bad) errors[`extraBirthday_${i}`] = bad;
  });
  // A court renter answers none of this: no birthday, no volleyball experience,
  // no training goals, no allergies. Requiring it would block them on a form
  // written for players.
  if (form.accountType === 'renter') return errors;
  if (form.accountType === 'junior') {
    if (!form.athleteFirstName.trim()) errors.athleteFirstName = 'Required';
    if (!form.athleteLastName.trim()) errors.athleteLastName = 'Required';
    if (!form.athleteBirthday) errors.athleteBirthday = 'Required';
    else {
      const bad = birthdayError(form.athleteBirthday, { athleteType: 'junior' });
      if (bad) errors.athleteBirthday = bad;
    }
    if (!form.athleteSchool.trim()) errors.athleteSchool = 'Required';
    if (!form.athleteGrade) errors.athleteGrade = 'Required';
    if (!form.athleteShirtSize) errors.athleteShirtSize = 'Required';
    if (!form.athleteGender) errors.athleteGender = 'Required';
  }
  if (form.accountType === 'adult') {
    if (!form.athleteBirthday) errors.athleteBirthday = 'Required';
    else {
      const bad = birthdayError(form.athleteBirthday, { athleteType: 'adult' });
      if (bad) errors.athleteBirthday = bad;
    }
    if (!form.athleteGender) errors.athleteGender = 'Required';
  }
  if (!form.indoorExperience) errors.indoorExperience = 'Required';
  if (!form.beachExperience) errors.beachExperience = 'Required';
  if (form.goals.length === 0) errors.goals = 'Please select at least one training goal';
  if (!form.allergies.trim()) errors.allergies = 'Required';
  if (!form.medicalConditions.trim()) errors.medicalConditions = 'Required';
  return errors;
}

function Register() {
  const navigate = useNavigate();
  const location = useLocation();
  // Preview mode: admin-only walkthrough of the registration flow without DB writes.
  // Triggered via /register-preview route OR by ?preview=1 query param.
  const previewMode =
    location.pathname === '/register-preview' ||
    new URLSearchParams(location.search).get('preview') === '1';
  // Preview is an admin tool — bounce non-admin (or logged-out) visitors home.
  useEffect(() => {
    if (!previewMode) return;
    const verifyAdmin = async () => {
      const u = auth.currentUser;
      if (!u) { navigate('/'); return; }
      try {
        const snap = await getDoc(doc(db, 'users', u.uid));
        const roles = snap.exists() ? (snap.data().roles || []) : [];
        if (!roles.includes('admin')) navigate('/');
      } catch (e) { navigate('/'); }
    };
    verifyAdmin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewMode]);
  const [activeStep, setActiveStep] = useState(0);
  const [inviteData, setInviteData] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [schools, setSchools] = useState([]);
  const [memberList, setMemberList] = useState([]);
  const [staffList, setStaffList] = useState([]);

  useEffect(() => {
    const fetchSchools = async () => {
      try {
        const snap = await getDoc(doc(db, 'settings', 'schools'));
        if (snap.exists()) setSchools(snap.data().list || []);
      } catch (e) { console.error('Failed to load schools', e); logError('Register', 'Failed to load schools: ' + e.message); }
    };
    fetchSchools();

    // Fetch coaches and athletes for referred by dropdown
    const fetchReferralLists = async () => {
      try {
        const [coachSnap, athleteSnap] = await Promise.all([
          getDocs(query(collection(db, 'users'), where('roles', 'array-contains-any', ['coach', 'assistant_coach']))),
          getDocs(query(collection(db, 'athletes'), where('status', '==', 'active'))),
        ]);
        setStaffList(coachSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setMemberList(athleteSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) { console.error('Failed to load referral lists', e); }
    };
    fetchReferralLists();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    // Fall back to the stashed token. Login stashes it before signing in, but
    // the /login route sends a newly-created unregistered user to a bare
    // "/register" — only RootRedirect (on "/") ever put it back on the URL, and
    // signing up from the login page never passes through "/". The token was
    // stashed and then never read, so an invited person landed on the ordinary
    // account-type chooser as if they had walked in off the street.
    const token = params.get('token') || sessionStorage.getItem('inviteToken');
    if (!token) return;
    const lookupInvite = async () => {
      try {
        const inviteDoc = await getDoc(doc(db, 'invites', token));
        if (!inviteDoc.exists()) return;
        const inv = inviteDoc.data();
        if (inv.used) return;
        if (inv.expiresAt?.toDate() < new Date()) return;
        setInviteData({ ...inv, token });
        // Cleared only now it is in hand — clearing on read would burn the
        // token if the lookup failed once (offline, rules hiccup) and leave
        // nothing to retry with.
        sessionStorage.removeItem('inviteToken');
      } catch (e) { console.error('Invite lookup failed', e); }
    };
    lookupInvite();
  }, [location.search]);

  const pendingInviteRef = React.useRef(null);

  const completeCoachRegistration = React.useCallback(async (user, invite) => {
    if (!user || !invite) return;
    setAutoRegistering(true);
    try {
      const coachDoc = await getDoc(doc(db, 'users', invite.coachId));
      const coachData = coachDoc.exists() ? coachDoc.data() : {};
      // Preserve any roles already on the pending doc (coach/assistant_coach/
      // contractor/volunteer), defaulting sensibly.
      const existingRoles = Array.isArray(coachData.roles) ? coachData.roles : [];
      const roles = existingRoles.length
        ? existingRoles
        : (coachData.isVolunteer ? ['volunteer'] : ['coach']);
      // Migrate the pending_ doc's data into the real UID doc (don't leave a ghost).
      await setDoc(doc(db, 'users', user.uid), {
        ...coachData,
        uid: user.uid, email: user.email, displayName: user.displayName || '',
        firstName: coachData.firstName || '', lastName: coachData.lastName || '',
        phone: coachData.phone || '', roles, linkedAthletes: coachData.linkedAthletes || [],
        authProvider: coachData.authProvider || user.providerData?.[0]?.providerId || 'password', createdAt: coachData.createdAt || new Date(),
        notifyByEmail: coachData.notifyByEmail ?? true, notifyBySMS: coachData.notifyBySMS ?? false,
        registrationComplete: true,
        pendingLinkEmail: false,
        migratedFrom: invite.coachId,
      }, { merge: true });
      // Repoint work entries keyed to the old pending id, then delete the ghost —
      // only if the ids actually differ (invite.coachId is the pending_ id).
      if (invite.coachId && invite.coachId !== user.uid) {
        try {
          const weSnap = await getDocs(query(collection(db, 'workEntries'), where('workerId', '==', invite.coachId)));
          for (const d of weSnap.docs) {
            await updateDoc(doc(db, 'workEntries', d.id), { workerId: user.uid });
          }
        } catch (mErr) { console.warn('work entry repoint failed:', mErr); }
        try { await deleteDoc(doc(db, 'users', invite.coachId)); }
        catch (dErr) { console.warn('pending doc delete failed:', dErr); }
      }
      await updateDoc(doc(db, 'invites', invite.token), { used: true, usedAt: new Date(), usedByUid: user.uid });
      navigate('/coach-dashboard');
    } catch (e) {
      console.error('Coach auto-registration failed:', e);
      logError('Register', 'Coach auto-registration failed: ' + e.message, e.stack);
      setAutoRegistering(false);
    }
  }, [navigate]);

  // A coach invite auto-completes registration from the pending doc. A GROUP
  // RENTAL invite must not: the renter has to confirm their own liability
  // insurance, and that acceptance is the only record that they agreed — an
  // auto-migration would skip the waiver step entirely. Renter tokens instead
  // prefill the form below and let the person walk it.
  const isRenterInvite = inviteData?.kind === 'renter';

  useEffect(() => {
    if (!inviteData || isRenterInvite) return;
    pendingInviteRef.current = inviteData;
    const user = auth.currentUser;
    if (user) completeCoachRegistration(user, inviteData);
  }, [inviteData, isRenterInvite, completeCoachRegistration]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user && pendingInviteRef.current) completeCoachRegistration(user, pendingInviteRef.current);
    });
    return () => unsubscribe();
  }, [completeCoachRegistration]);

  // Seed the renter's details from the invite so they confirm rather than retype
  // what the club already recorded. Account type is forced: the invite is what
  // authorises a rental account, so it is not theirs to change.
  useEffect(() => {
    if (!isRenterInvite) return;
    setForm(f => ({
      ...f,
      accountType: 'renter',
      organizationName: f.organizationName || inviteData.organizationName || '',
      firstName: f.firstName || inviteData.contactFirstName || '',
      lastName: f.lastName || inviteData.contactLastName || '',
      phone: f.phone || inviteData.phone || '',
    }));
    setActiveStep(step => (step === 0 ? 1 : step));
  }, [isRenterInvite, inviteData]);

  const [autoRegistering, setAutoRegistering] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState('');
  const [dupCandidate, setDupCandidate] = useState(null);
  // The link is proved by a code sent to the OTHER account's address, so the
  // "is this you?" panel now has a second half: ask, then enter what arrived.
  const [linkSentTo, setLinkSentTo] = useState(null);   // masked address
  const [linkCode, setLinkCode] = useState('');
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkError, setLinkError] = useState('');

  const [form, setForm] = useState({
    accountType: '', firstName: '', lastName: '', phone: '', nickname: '', relationship: '', referredBy: '', referredBySource: '', referredByName: '', organizationName: '',
    address: '', city: '', state: 'FL', zip: '',
    guardian2FirstName: '', guardian2LastName: '', guardian2Phone: '', guardian2Email: '',
    athleteFirstName: '', athleteLastName: '', athleteBirthday: '',
    athleteGrade: '', athleteSchool: '', athleteShirtSize: '', athleteGender: '',
    indoorExperience: '', beachExperience: '', goals: [],
    allergies: 'None', medicalConditions: 'None',
    emergencyContactName: '', emergencyContactPhone: '', emergencyContactRelationship: '',
    holdHarmlessWaiver: false, mediaWaiver: false, smsConsent: false, liabilityInsurance: false,
  });

  // A paying member who never had a Torch login has a member shell
  // (users/mini_<emailkey>, from createStripeMemberShells) holding their name
  // and membership. absorbMiniAccount joins it to this account at submit; until
  // then the form looked like a stranger's sign-up and asked for everything
  // again. Seed what the shell knows and say the membership is waiting.
  const [memberShell, setMemberShell] = useState(null);
  useEffect(() => {
    const email = (auth.currentUser?.email || '').toLowerCase();
    if (!email) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(query(
          collection(db, 'users'),
          where('email', '==', email),
          where('registrationLevel', '==', 'mini')
        ));
        const shell = snap.docs.map(d => d.data()).find(d => d.membership?.stripeSubscriptionId);
        if (!shell || cancelled) return;
        setMemberShell(shell);
        setForm(f => ({
          ...f,
          firstName: f.firstName || shell.firstName || '',
          lastName: f.lastName || shell.lastName || '',
          phone: f.phone || shell.phone || '',
        }));
      } catch (e) { console.warn('Member shell lookup failed:', e); }
    })();
    return () => { cancelled = true; };
  }, []);

  const emptyAdditionalAthlete = { firstName: '', lastName: '', birthday: '', grade: '', school: '', shirtSize: '', gender: '', phone: '', nickname: '', indoorExperience: '', beachExperience: '', goals: [], allergies: '', medicalConditions: '' };
  const [additionalAthletes, setAdditionalAthletes] = useState([]);

  // Co-parent prefill: when a newly-signed-in user came via /co-parent-link
  // and clicked "Yes that's my family", they land here with
  // ?prefill=coparent&from=<guardian1Uid>. We pre-populate the form with
  // guardian2* data from guardian1's user doc so they don't re-type, and
  // we tell the flow to skip the "add child" step (athletes already linked
  // via parentUserIds array on the existing athlete docs).
  const [coParentMode, setCoParentMode] = useState(false);
  const [coParentFamilyName, setCoParentFamilyName] = useState('');
  const steps = coParentMode ? STEPS_COPARENT
    : form.accountType === 'renter' ? STEPS_RENTER : STEPS_DEFAULT;
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('prefill') !== 'coparent') return;
    const guardian1Uid = params.get('from');
    if (!guardian1Uid) return;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'users', guardian1Uid));
        if (!snap.exists()) return;
        const g1 = snap.data();
        setCoParentMode(true);
        setCoParentFamilyName(`${g1.firstName || ''} ${g1.lastName || ''}`.trim());
        setForm(prev => ({
          ...prev,
          // 'junior' = parent-of-junior path (gives us the right form sections);
          // we'll skip the athlete-creation portion via coParentMode flag.
          accountType: 'junior',
          firstName: g1.guardian2FirstName || prev.firstName,
          lastName: g1.guardian2LastName || prev.lastName,
          phone: g1.guardian2Phone ? formatPhone(g1.guardian2Phone) : prev.phone,
          // Default relationship to "Parent" so the validator doesn't block
          relationship: prev.relationship || 'Parent',
          // Address likely shared between co-parents in the same household
          address: g1.address || prev.address,
          city: g1.city || prev.city,
          state: g1.state || prev.state,
          zip: g1.zip || prev.zip,
        }));
        // Skip past the accountType picker since we know they're a parent
        setActiveStep(1);
      } catch (e) {
        console.error('Co-parent prefill failed:', e);
      }
    })();
  }, [location.search]);

  const updateAdditionalAthlete = (index, field, value) => {
    setAdditionalAthletes(prev => prev.map((a, i) => i === index ? { ...a, [field]: value } : a));
  };

  const update = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const toggleGoal = (goal) => {
    setForm(prev => ({
      ...prev,
      goals: prev.goals.includes(goal) ? prev.goals.filter(g => g !== goal) : [...prev.goals, goal]
    }));
    if (fieldErrors.goals) setFieldErrors(prev => ({ ...prev, goals: undefined }));
  };

  const handleNext = () => {
    setError('');
    if (activeStep === 0 && !form.accountType) { setError('Please select an account type.'); return; }
    if (activeStep === 1) {
      const errors = validateStep1(form, coParentMode);
      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        setError('Please fill in all required fields.');
        return;
      }
    }
    if (activeStep === 2) {
      // Co-parents don't add athlete info — athletes are already linked.
      // Skip the validator (which requires athleteFirstName, beach experience, etc.)
      if (!coParentMode) {
        const errors = validateStep2(form, additionalAthletes);
        if (Object.keys(errors).length > 0) {
          setFieldErrors(errors);
          setError('Please fill in all required fields.');
          return;
        }
      }
    }
    if (activeStep === 3) {
      if (!form.holdHarmlessWaiver) { setError('You must accept the Liability & Media Release Agreement to continue.'); return; }
      if (form.accountType === 'renter' && !form.liabilityInsurance) {
        setError('Court renters must confirm they carry their own liability insurance.'); return;
      }
    }
    setFieldErrors({});
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
    setFieldErrors({});
    setError('');
  };

  // Second half of the link: the code that landed in the other account's inbox.
  // On success the server has already written the pointer, so the only thing
  // left is to get a fresh token and land on the dashboard as the merged member.
  const submitLinkCode = async () => {
    const code = linkCode.replace(/\D/g, '');
    if (code.length !== 6) { setLinkError('Enter the six-digit code from the email.'); return; }
    setLinkBusy(true); setLinkError('');
    try {
      const fn = httpsCallable(getFunctions(), 'confirmAccountLink');
      await fn({ code });
      await auth.currentUser.getIdToken(true);
      window.location.href = '/dashboard';
    } catch (e) {
      setLinkError(e.message || 'That code did not work.');
      setLinkBusy(false);
    }
  };

  const handleSubmit = async (mode) => {
    // Co-parents don't need to accept waivers — they're not athletes, and
    // their kids' waivers were already signed by Parent 1 at original registration.
    if (form.accountType === 'renter' && !form.liabilityInsurance) {
      setError('Court renters must confirm they carry their own liability insurance.');
      return;
    }
    if (!coParentMode && !form.holdHarmlessWaiver) {
      setError('You must accept the Liability & Media Release Agreement.');
      return;
    }
    if (previewMode) {
      // Don't save anything — redirect to welcome preview to see the full flow
      setSaving(true);
      // Stash the form data in sessionStorage so Welcome page can show realistic preview content
      sessionStorage.setItem('previewFormData', JSON.stringify(form));
      setTimeout(() => {
        setSaving(false);
        navigate('/welcome?preview=1');
      }, 400);
      return;
    }
    setSaving(true);
    setDupCandidate(null);
    setNotice('');
    try {
      const user = auth.currentUser;
      if (!user) { setError('Not logged in.'); setSaving(false); return; }

      // Saying "that's me" is a claim, and name + surname + phone are all
      // readable from inside the app — so it is not proof of anything. The
      // pointer is written by confirmAccountLink, on the server, only once a
      // code sent to the OTHER account's address comes back. This step just
      // asks for that code to be sent; the panel then collects it.
      if (mode === 'link' && dupCandidate) {
        setLinkBusy(true); setLinkError('');
        try {
          const fn = httpsCallable(getFunctions(), 'requestAccountLink');
          const r = await fn({ targetUserId: dupCandidate.id });
          setLinkSentTo(r.data.sentTo);
        } catch (e) {
          setLinkError(e.message || 'Could not send the code.');
        } finally { setLinkBusy(false); setSaving(false); }
        return;
      }

      const existingDoc = await getDoc(doc(db, 'users', user.uid));
      if (existingDoc.exists() && existingDoc.data().registrationComplete) {
        setNotice("You're already registered \u2014 taking you to your dashboard\u2026");
        await auth.currentUser.getIdToken(true);
        setTimeout(() => { window.location.href = '/dashboard'; }, 1600);
        return;
      }

      // Possible-duplicate check: same first + last name + phone as an existing
      // member usually means they already have an account under a different
      // email. Pause and ask them to confirm (dupCandidate panel). Skipped for
      // co-parents (intentional linking) and when they chose "create new".
      if (mode !== 'new' && !coParentMode) {
        const norm = (v) => (v || '').toLowerCase().trim();
        const digits = (v) => (v || '').replace(/\D/g, '').slice(-10);
        const fn = norm(form.firstName), ln = norm(form.lastName), ph = digits(form.phone);
        if (fn && ln && ph) {
          const usersSnap = await getDocs(collection(db, 'users'));
          const match = usersSnap.docs.find(d => {
            if (d.id === user.uid) return false;
            const u = d.data();
            if (u.mergedIntoUid) return false; // skip alias stubs
            return norm(u.firstName) === fn && norm(u.lastName) === ln && digits(u.phone) === ph;
          });
          if (match) { setDupCandidate({ id: match.id, ...match.data() }); setSaving(false); return; }
        }
      }
      const now = new Date();
      const gradYear = form.athleteGrade
        ? new Date().getFullYear() + (12 - parseInt(form.athleteGrade)) + (new Date().getMonth() >= 5 ? 1 : 0)
        : null;
      // The user doc is written by completeRegistration, not here.
      //
      // Doing it from the browser only ever worked for people whose doc did not
      // already exist: firestore.rules forbids a non-admin from changing roles
      // or gateAccess on a user doc, and a setDoc without merge on an existing
      // doc does both — it rewrites roles and drops gateAccess by omission. So
      // an existing member finishing registration got permission-denied here,
      // every time, and the app sent them back to /register because the doc had
      // no registrationComplete. The server derives the roles now; the account
      // type is all it takes from us.
      const complete = httpsCallable(getFunctions(), 'completeRegistration');
      await complete({
        accountType: form.accountType,
        firstName: form.firstName,
        lastName: form.lastName,
        phone: form.phone,
        displayName: user.displayName || '',
        authProvider: user.providerData?.[0]?.providerId || 'password',
        address: form.address, city: form.city, state: form.state, zip: form.zip,
        nickname: cleanName(form.nickname),
        relationship: form.relationship || '',
        ...(form.organizationName ? { organizationName: form.organizationName.trim() } : {}),
        referredBy: form.referredBySource
          ? (form.referredByName ? `${form.referredBySource} - ${form.referredByName}` : form.referredBySource)
          : '',
        guardian2FirstName: cleanName(form.guardian2FirstName),
        guardian2LastName: cleanName(form.guardian2LastName),
        guardian2Phone: form.guardian2Phone,
        guardian2Email: form.guardian2Email,
        ...(form.accountType === 'renter' ? {
          holdHarmlessWaiver: form.holdHarmlessWaiver,
          mediaWaiver: form.mediaWaiver,
          liabilityInsurance: form.liabilityInsurance,
        } : {}),
      });

      // A court renter is given their tier server-side — the client must not
      // write membership, or anyone could register and grant themselves Gold.
      if (form.accountType === 'renter') {
        try {
          const provision = httpsCallable(getFunctions(), 'provisionCourtRenter');
          // The token decides the rate tier; without it the server falls back
          // to the club's default court-rental tier.
          await provision(isRenterInvite ? { inviteToken: inviteData.token } : {});
        } catch (e) {
          // Non-fatal: they are registered either way, and the admin
          // notification already asks someone to assign a tier.
          console.warn('Court renter tier assignment failed:', e);
        }
      }

      // Absorb a mini account left by an earlier day-pass purchase. Done in a
      // callable because repointing purchases needs admin rights — the same
      // wall the phone-match merge hit above. Failure is non-fatal: the person
      // is registered either way, an admin can merge later.
      try {
        const absorb = httpsCallable(getFunctions(), 'absorbMiniAccount');
        await absorb({});
      } catch (mErr) { console.warn('mini account absorb failed:', mErr); }
      if (inviteData?.token) await updateDoc(doc(db, 'invites', inviteData.token), { used: true, usedAt: new Date(), usedByUid: user.uid });
      if (form.accountType === 'junior' && !coParentMode) {
        const athleteId = `ath_${user.uid}_${Date.now()}`;
        await setDoc(doc(db, 'athletes', athleteId), {
          athleteId, parentUserId: user.uid, parentUserIds: [user.uid],
          firstName: cleanName(form.athleteFirstName), lastName: cleanName(form.athleteLastName),
          birthday: form.athleteBirthday, athleteType: 'junior', skillGroup: beachToSkillGroup(form.beachExperience),
          schoolName: form.athleteSchool, currentGrade: parseInt(form.athleteGrade),
          graduationYear: gradYear, indoorExperience: form.indoorExperience,
          beachExperience: form.beachExperience, shirtSize: form.athleteShirtSize, nickname: cleanName(form.athleteNickname), phone: form.athletePhone || '', gender: form.athleteGender || '',
          goals: form.goals, sessionBalance: 0, weeklyBalance: 0,
          emergencyContact: { name: form.emergencyContactName, phone: form.emergencyContactPhone, relationship: form.emergencyContactRelationship },
          allergies: form.allergies || 'None', medicalConditions: form.medicalConditions || 'None',
          waiverSigned: form.holdHarmlessWaiver, mediaWaiverSigned: form.mediaWaiver,
          smsConsent: form.smsConsent, waiverDate: now, registrationDate: now,
        });
        for (const extra of additionalAthletes) {
          if (!extra.firstName.trim() || !extra.lastName.trim()) continue;
          const extraGradYear = extra.grade
            ? new Date().getFullYear() + (12 - parseInt(extra.grade)) + (new Date().getMonth() >= 5 ? 1 : 0)
            : null;
          const extraId = `ath_${user.uid}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
          await setDoc(doc(db, 'athletes', extraId), {
            athleteId: extraId, parentUserId: user.uid, parentUserIds: [user.uid],
            firstName: cleanName(extra.firstName), lastName: cleanName(extra.lastName), nickname: cleanName(extra.nickname),
            phone: extra.phone || '', gender: extra.gender || '',
            birthday: extra.birthday, athleteType: 'junior',
            skillGroup: beachToSkillGroup(extra.beachExperience || ''),
            schoolName: extra.school, currentGrade: parseInt(extra.grade) || 0,
            graduationYear: extraGradYear, shirtSize: extra.shirtSize,
            indoorExperience: extra.indoorExperience || '', beachExperience: extra.beachExperience || '',
            goals: extra.goals || [], sessionBalance: 0, weeklyBalance: 0,
            emergencyContact: { name: form.emergencyContactName, phone: form.emergencyContactPhone, relationship: form.emergencyContactRelationship },
            allergies: extra.allergies || 'None', medicalConditions: extra.medicalConditions || 'None',
            waiverSigned: form.holdHarmlessWaiver, mediaWaiverSigned: form.mediaWaiver,
            smsConsent: form.smsConsent, waiverDate: now, registrationDate: now,
          });
        }
      } else if (!coParentMode && form.accountType !== 'renter') {
        // Renters get no athlete document: they never check in, never hold a
        // session balance, and an athlete row would put them on rosters and
        // in the Juniors/athlete listings they have no business being in.
        const athleteId = `ath_${user.uid}_${Date.now()}`;
        await setDoc(doc(db, 'athletes', athleteId), {
          athleteId, parentUserId: null, userId: user.uid,
          firstName: cleanName(form.firstName), lastName: cleanName(form.lastName),
          email: user.email || '',
          phone: form.phone || '',
          address: form.address || '', city: form.city || '', state: form.state || '', zip: form.zip || '',
          birthday: form.athleteBirthday || '', gender: form.athleteGender || '', athleteType: 'adult', skillGroup: beachToSkillGroup(form.beachExperience),
          indoorExperience: form.indoorExperience, beachExperience: form.beachExperience,
          goals: form.goals, sessionBalance: 0, weeklyBalance: 0,
          emergencyContact: { name: form.emergencyContactName, phone: form.emergencyContactPhone, relationship: form.emergencyContactRelationship },
          allergies: form.allergies || 'None', medicalConditions: form.medicalConditions || 'None',
          waiverSigned: form.holdHarmlessWaiver, mediaWaiverSigned: form.mediaWaiver,
          smsConsent: form.smsConsent, waiverDate: now, registrationDate: now,
        });
      }
      // Co-parents skip athlete creation entirely — their athletes already
      // exist under Parent 1's account, and Phase B linked them via
      // parentUserIds arrayUnion during /co-parent-link confirmation.
      // Send confirmation + admin notification emails
      try {
        const functions = getFunctions();
        const sendEmails = httpsCallable(functions, 'sendRegistrationEmails');
        await sendEmails({
          uid: user.uid,
          accountType: form.accountType,
          parentFirstName: form.firstName,
          parentEmail: user.email,
          athleteFirstName: form.accountType === 'junior' ? form.athleteFirstName : form.firstName,
          athleteLastName: form.accountType === 'junior' ? form.athleteLastName : form.lastName,
          // The plan they arrived on, for the admin alert. Read, not cleared —
          // the post-registration redirect below still needs it, and this is
          // interest rather than a purchase: checkout has not happened yet.
          planId: sessionStorage.getItem('selectedPlanId') || '',
        });
      } catch (e) {
        console.warn('Registration email failed (non-blocking):', e);
      }

      // Check for post-registration destination
      // Return to pre-login destination if set, otherwise dashboard
      const postRegReturn = sessionStorage.getItem('postRegReturnTo');
      const savedPlan = sessionStorage.getItem('selectedPlanId');
      if (postRegReturn) {
        sessionStorage.removeItem('postRegReturnTo');
        window.location.href = postRegReturn;
      } else if (savedPlan) {
        // Plan was selected before login — go to training-plans
        window.location.href = '/training-plans';
      } else if (form.accountType === 'renter') {
        // Straight to the calendar: their tier came from the invite, so booking
        // works the moment they land. /welcome is written for players — session
        // packs and practices — and would be noise to a group renting courts.
        window.location.href = '/schedule';
      } else {
        // First-time registration — show welcome page
        window.location.href = '/welcome';
      }
    } catch (err) {
      console.error('Registration error:', err.code, err.message, err);
      logError('Register', err.message || err.code, JSON.stringify({ code: err.code, step: activeStep }));
      setError('Something went wrong: ' + (err.message || err.code || 'Unknown error'));
      setSaving(false);
    }
  };

  const fe = (field) => ({
    error: !!fieldErrors[field],
    helperText: fieldErrors[field] || '',
  });

  const se = (field) => ({
    error: !!fieldErrors[field],
  });

  const renderStep = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 3, color: 'white', fontWeight: 'bold', fontFamily: '"Bebas Neue", sans-serif', letterSpacing: '2px', fontSize: '1.2rem' }}>
              Who are you registering?
            </Typography>
            <Grid container spacing={2}>
              {[
                { value: 'junior', label: 'Junior Athlete', desc: 'Registering a child / junior player' },
                { value: 'adult', label: 'Adult Athlete', desc: 'Registering myself as an adult player' },
                // Court Renter is invite-only: a rental account carries
                // court-booking rights at the rental rate, so it is granted by
                // an admin invite rather than self-selected. The option is only
                // offered when a valid renter invite token is present.
                ...(isRenterInvite
                  ? [{ value: 'renter', label: 'Court Renter', desc: 'Booking courts for a business or group — not joining as a player' }]
                  : []),
              ].map((opt) => (
                <Grid size={{ xs: 12, sm: 6 }} key={opt.value}>
                  <Paper onClick={() => { update('accountType', opt.value); setActiveStep(1); }}
                    elevation={form.accountType === opt.value ? 4 : 1}
                    sx={{
                      p: 3, borderRadius: 2, cursor: 'pointer', textAlign: 'center',
                      border: form.accountType === opt.value ? '2px solid #2E6DA4' : '2px solid transparent',
                      backgroundColor: form.accountType === opt.value ? '#DEEAF1' : '#eeeeee',
                    }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1E3A5F' }}>{opt.label}</Typography>
                    <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>{opt.desc}</Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 3, color: 'white', fontWeight: 'bold', fontFamily: '"Bebas Neue", sans-serif', letterSpacing: '2px', fontSize: '1.2rem' }}>
              {form.accountType === 'junior' ? 'Parent / Guardian Information' : 'Your Information'}
            </Typography>
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField fullWidth sx={{ backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: 1 }} label="First Name *" value={form.firstName}
                    onChange={e => update('firstName', e.target.value)} {...fe('firstName')} />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField fullWidth sx={{ backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: 1 }} label="Last Name *" value={form.lastName}
                    onChange={e => update('lastName', e.target.value)} {...fe('lastName')} />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField fullWidth sx={{ backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: 1 }} label="Phone *" value={form.phone}
                    onChange={e => update('phone', formatPhone(e.target.value))} {...fe('phone')} />
                </Grid>
                {form.accountType === 'junior' && (
                  <>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField fullWidth sx={{ backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: 1 }} label="Nickname" value={form.nickname}
                        onChange={e => update('nickname', e.target.value)} />
                    </Grid>
                    {form.accountType === 'junior' && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormControl fullWidth sx={{ backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: 1 }} error={!!fieldErrors.relationship}>
                        <InputLabel>Relationship to Athlete *</InputLabel>
                        <Select value={form.relationship} label="Relationship to Athlete *"
                          onChange={e => update('relationship', e.target.value)}>
                          {['Mother', 'Father', 'Guardian', 'Grandparent', 'Other'].map(r => (
                            <MenuItem key={r} value={r}>{r}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    )}
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField fullWidth select sx={{ backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: 1 }} label="Referred By"
                        value={form.referredBySource || ''} onChange={e => update('referredBySource', e.target.value)}>
                        <MenuItem value="">Select...</MenuItem>
                        <MenuItem value="web_search">Web Search</MenuItem>
                        <MenuItem value="social_media">Social Media</MenuItem>
                        <MenuItem value="friend">Friend</MenuItem>
                        <MenuItem value="coach">Coach</MenuItem>
                        <MenuItem value="existing_member">Existing Member</MenuItem>
                      </TextField>
                    </Grid>
                    {(form.referredBySource === 'coach') && (
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField fullWidth select sx={{ backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: 1 }} label="Which Coach?"
                          value={form.referredByName || ''} onChange={e => update('referredByName', e.target.value)}>
                          <MenuItem value="">Select coach...</MenuItem>
                          {staffList.map(c => (
                            <MenuItem key={c.id} value={`${c.firstName} ${c.lastName}`}>{c.firstName} {c.lastName}</MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    )}
                    {(form.referredBySource === 'existing_member') && (
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField fullWidth select sx={{ backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: 1 }} label="Which Member?"
                          value={form.referredByName || ''} onChange={e => update('referredByName', e.target.value)}>
                          <MenuItem value="">Select member...</MenuItem>
                          {memberList.map(m => (
                            <MenuItem key={m.id} value={`${m.firstName} ${m.lastName}`}>{m.firstName} {m.lastName}</MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    )}
                  </>
                )}
                <Grid size={{ xs: 12 }}>
                  <TextField fullWidth sx={{ backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: 1 }} label="Address *" value={form.address}
                    onChange={e => update('address', e.target.value)} {...fe('address')} />
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <TextField fullWidth sx={{ backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: 1 }} label="Zip *" value={form.zip}
                    onChange={async e => {
                      const zip = e.target.value.replace(/\D/g, '').slice(0, 5);
                      update('zip', zip);
                      if (zip.length === 5) {
                        const result = await lookupZip(zip);
                        if (result) {
                          update('city', result.city);
                          update('state', result.state);
                        }
                      }
                    }} {...fe('zip')} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField fullWidth sx={{ backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: 1 }} label="City *" value={form.city}
                    onChange={e => update('city', e.target.value)} {...fe('city')} />
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <TextField fullWidth sx={{ backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: 1 }} label="State *" value={form.state}
                    onChange={e => update('state', e.target.value)} {...fe('state')} />
                </Grid>

                {form.accountType === 'junior' && !coParentMode && (
                  <>
                    <Grid size={{ xs: 12 }}>
                      <Divider sx={{ my: 1 }}>
                        <Typography variant="caption" sx={{ color: '#888' }}>Parent / Guardian 2 (Optional)</Typography>
                      </Divider>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField fullWidth sx={{ backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: 1 }} label="First Name" value={form.guardian2FirstName} onChange={e => update('guardian2FirstName', e.target.value)} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField fullWidth sx={{ backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: 1 }} label="Last Name" value={form.guardian2LastName} onChange={e => update('guardian2LastName', e.target.value)} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField fullWidth sx={{ backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: 1 }} label="Phone" value={form.guardian2Phone} onChange={e => update('guardian2Phone', formatPhone(e.target.value))} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField fullWidth sx={{ backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: 1 }} label="Email" value={form.guardian2Email} onChange={e => update('guardian2Email', e.target.value)} />
                    </Grid>
                  </>
                )}

                {!coParentMode && (
                  <>
                    <Grid size={{ xs: 12 }}>
                      <Divider sx={{ my: 1 }}>
                        <Typography variant="caption" sx={{ color: '#888' }}>Emergency Contact</Typography>
                      </Divider>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField fullWidth sx={{ backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: 1 }} label="Emergency Contact Name *" value={form.emergencyContactName}
                        onChange={e => update('emergencyContactName', e.target.value)} {...fe('emergencyContactName')} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField fullWidth sx={{ backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: 1 }} label="Emergency Contact Phone *" value={form.emergencyContactPhone}
                        onChange={e => update('emergencyContactPhone', formatPhone(e.target.value))} {...fe('emergencyContactPhone')} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormControl fullWidth sx={{ backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: 1 }} error={!!fieldErrors.emergencyContactRelationship}>
                        <InputLabel>Relationship *</InputLabel>
                        <Select value={form.emergencyContactRelationship} label="Relationship *"
                          onChange={e => update('emergencyContactRelationship', e.target.value)}>
                          {['Mother', 'Father', 'Guardian', 'Grandparent', 'Spouse', 'Friend', 'Other'].map(r => (
                            <MenuItem key={r} value={r}>{r}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </>
                )}
            </Grid>
          </Box>
        );

      case 2:
        if (coParentMode) {
          return (
            <Box>
              <Typography variant="h6" sx={{ mb: 2, color: 'white', fontWeight: 'bold', fontFamily: '"Bebas Neue", sans-serif', letterSpacing: '2px', fontSize: '1.2rem' }}>
                Athlete Information
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.7)', mb: 2 }}>
                You're already linked to the {coParentFamilyName.split(' ').pop() || ''} family's athletes — no need to add them again.
                Click <strong>Next</strong> to continue.
              </Typography>
            </Box>
          );
        }
        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 3, color: 'white', fontWeight: 'bold', fontFamily: '"Bebas Neue", sans-serif', letterSpacing: '2px', fontSize: '1.2rem' }}>
              {form.accountType === 'junior' ? 'Athlete Information'
                : form.accountType === 'renter' ? 'Booking Details' : 'Additional Information'}
            </Typography>
            {form.accountType === 'renter' && (
              <Box sx={{ mb: 2 }}>
                {/* Was backgroundColor 'white' on a dark form: the label is
                    light-on-dark like every other field here, so the part of it
                    sitting over the white box was invisible. Matching the shared
                    translucent fill fixes it; shrink keeps the label in the
                    border notch whether or not the invite prefilled a value. */}
                <TextField
                  fullWidth label="Business or group name"
                  value={form.organizationName || ''}
                  onChange={e => update('organizationName', e.target.value)}
                  placeholder="e.g. Beach Tennis"
                  InputLabelProps={{ shrink: true }}
                  sx={{ backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: 1, mb: 2 }}
                />
                <Alert severity="success">
                  That's everything we need. Your court booking is already set up —
                  finish below and you can start booking straight away.
                </Alert>
              </Box>
            )}
            <Grid container spacing={2}>
                {form.accountType === 'junior' && (
                  <>
                    {/* Row 1: First Name, Last Name, Nickname */}
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <TextField fullWidth sx={{ backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: 1 }} label="First Name *" value={form.athleteFirstName}
                        onChange={e => update('athleteFirstName', e.target.value)} {...fe('athleteFirstName')} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <TextField fullWidth sx={{ backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: 1 }} label="Last Name *" value={form.athleteLastName}
                        onChange={e => update('athleteLastName', e.target.value)} {...fe('athleteLastName')} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <TextField fullWidth sx={{ backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: 1 }} label="Nickname" value={form.athleteNickname || ''}
                        onChange={e => update('athleteNickname', e.target.value)} />
                    </Grid>

                    {/* Row 2: Date of Birth, Gender, Phone */}
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <TextField fullWidth sx={{ backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: 1 }} label="Date of Birth *" type="date" InputLabelProps={{ shrink: true }}
                        value={form.athleteBirthday} onChange={e => update('athleteBirthday', e.target.value)} {...fe('athleteBirthday')} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <FormControl fullWidth sx={{ backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: 1 }} error={!!fieldErrors.athleteGender}>
                        <InputLabel id="gender-label">Gender *</InputLabel>
                        <Select labelId="gender-label" value={form.athleteGender || ''} label="Gender *"
                          onChange={e => update('athleteGender', e.target.value)}>
                          <MenuItem value="Female">Female</MenuItem>
                          <MenuItem value="Male">Male</MenuItem>
                          <MenuItem value="Non-binary">Non-binary</MenuItem>
                          <MenuItem value="Prefer not to say">Prefer not to say</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <TextField fullWidth sx={{ backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: 1 }} label="Athlete Phone"
                        value={form.athletePhone || ''} onChange={e => update('athletePhone', formatPhone(e.target.value))}
                        helperText="Used for event updates only" />
                    </Grid>

                    {/* Row 3: School, Grade, Shirt Size */}
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Autocomplete
                        options={schools}
                        value={form.athleteSchool || null}
                        onChange={(_, newValue) => update('athleteSchool', newValue || '')}
                        freeSolo
                        renderInput={(params) => (
                          <TextField {...params} fullWidth sx={{ backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: 1 }} label="School Name *"
                            onChange={e => update('athleteSchool', e.target.value)}
                            {...fe('athleteSchool')} />
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <FormControl fullWidth sx={{ backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: 1 }} {...se('athleteGrade')}>
                        <InputLabel id="grade-label">Current Grade *</InputLabel>
                        <Select labelId="grade-label" value={form.athleteGrade} label="Current Grade *"
                          onChange={e => update('athleteGrade', e.target.value)}>
                          {[...Array(8)].map((_, i) => (
                            <MenuItem key={i + 5} value={i + 5}>{`Grade ${i + 5}`}</MenuItem>
                          ))}
                        </Select>
                        {fieldErrors.athleteGrade && <FormHelperText>{fieldErrors.athleteGrade}</FormHelperText>}
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <FormControl fullWidth sx={{ backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: 1 }} {...se('athleteShirtSize')}>
                        <InputLabel id="shirt-label">Shirt Size *</InputLabel>
                        <Select labelId="shirt-label" value={form.athleteShirtSize} label="Shirt Size *"
                          onChange={e => update('athleteShirtSize', e.target.value)}>
                          {shirtSizes.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                        </Select>
                        {fieldErrors.athleteShirtSize && <FormHelperText>{fieldErrors.athleteShirtSize}</FormHelperText>}
                      </FormControl>
                    </Grid>
                  </>
                )}
                {form.accountType === 'adult' && (
                  <>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <TextField fullWidth sx={{ backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: 1 }} label="Date of Birth *" type="date"
                        InputLabelProps={{ shrink: true }} value={form.athleteBirthday}
                        onChange={e => update('athleteBirthday', e.target.value)} {...fe('athleteBirthday')} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <FormControl fullWidth sx={{ backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: 1 }} error={!!fieldErrors.athleteGender}>
                        <InputLabel>Gender *</InputLabel>
                        <Select value={form.athleteGender || ''} label="Gender *"
                          onChange={e => update('athleteGender', e.target.value)}>
                          <MenuItem value="Female">Female</MenuItem>
                          <MenuItem value="Male">Male</MenuItem>
                          <MenuItem value="Non-binary">Non-binary</MenuItem>
                          <MenuItem value="Prefer not to say">Prefer not to say</MenuItem>
                        </Select>
                        {fieldErrors.athleteGender && <FormHelperText>{fieldErrors.athleteGender}</FormHelperText>}
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }} />
                  </>
                )}
                {/* Athlete questions only. A renter books courts and has no
                    athlete record, so volleyball experience, training goals and
                    medical details are neither asked nor stored for them. */}
                {form.accountType !== 'renter' && (<>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FormControl fullWidth sx={{ backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: 1 }} {...se('indoorExperience')}>
                    <InputLabel id="indoor-label">Indoor Experience *</InputLabel>
                    <Select labelId="indoor-label" value={form.indoorExperience} label="Indoor Experience *"
                      onChange={e => update('indoorExperience', e.target.value)}>
                      {experienceLevels.map(l => <MenuItem key={l} value={l}>{l}</MenuItem>)}
                    </Select>
                    {fieldErrors.indoorExperience && <FormHelperText>{fieldErrors.indoorExperience}</FormHelperText>}
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FormControl fullWidth sx={{ backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: 1 }} {...se('beachExperience')}>
                    <InputLabel id="beach-label">Beach Experience / Class Level *</InputLabel>
                    <Select labelId="beach-label" value={form.beachExperience} label="Beach Experience / Class Level *"
                      onChange={e => update('beachExperience', e.target.value)}>
                      {beachLevels.map(l => <MenuItem key={l} value={l}>{l}</MenuItem>)}
                    </Select>
                    {fieldErrors.beachExperience && <FormHelperText>{fieldErrors.beachExperience}</FormHelperText>}
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }} />
                <Grid size={{ xs: 12 }}>
                  <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 'bold', color: fieldErrors.goals ? '#d32f2f' : '#444' }}>
                    Training Goals * {fieldErrors.goals && <span style={{ fontWeight: 'normal', fontSize: '0.75rem' }}>— {fieldErrors.goals}</span>}
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 1.5, borderColor: fieldErrors.goals ? '#d32f2f' : 'divider', borderRadius: 1 }}>
                    {(form.accountType === 'adult' ? adultGoalOptions : goalOptions).map(goal => (
                      <FormControlLabel key={goal}
                        control={<Checkbox checked={form.goals.includes(goal)} onChange={() => toggleGoal(goal)} />}
                        label={goal} sx={{ display: 'block' }} />
                    ))}
                  </Paper>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField fullWidth sx={{ backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: 1 }} label="Allergies *" value={form.allergies}
                    onChange={e => update('allergies', e.target.value)} placeholder="None" />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField fullWidth sx={{ backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: 1 }} label="Medical Conditions *" value={form.medicalConditions}
                    onChange={e => update('medicalConditions', e.target.value)} placeholder="None" />
                </Grid>
                </>)}
              </Grid>

              {form.accountType === 'junior' && (
                <Box sx={{ mt: 2 }}>
                  <Divider sx={{ my: 1 }} />
                  {additionalAthletes.map((athlete, index) => (
                    <Box key={index} sx={{
                      mb: 3, p: 2,
                      backgroundColor: 'rgba(255,255,255,0.03)',
                      borderRadius: 2,
                      border: '1px solid rgba(201,168,76,0.2)',
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#C9A84C', letterSpacing: '1px' }}>
                          Child #{index + 2}
                        </Typography>
                        <Button size="small" color="error" onClick={() => setAdditionalAthletes(prev => prev.filter((_, i) => i !== index))}>
                          Remove
                        </Button>
                      </Box>
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <TextField fullWidth sx={{ backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: 1 }} label="First Name *" value={athlete.firstName}
                            onChange={e => updateAdditionalAthlete(index, 'firstName', e.target.value)} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <TextField fullWidth sx={{ backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: 1 }} label="Last Name *" value={athlete.lastName}
                            onChange={e => updateAdditionalAthlete(index, 'lastName', e.target.value)} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <TextField fullWidth sx={{ backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: 1 }} label="Nickname"
                            value={athlete.nickname || ''} onChange={e => updateAdditionalAthlete(index, 'nickname', e.target.value)} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <TextField fullWidth sx={{ backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: 1 }} label="Date of Birth *" type="date" InputLabelProps={{ shrink: true }}
                            value={athlete.birthday} onChange={e => updateAdditionalAthlete(index, 'birthday', e.target.value)}
                            error={!!fieldErrors[`extraBirthday_${index}`]}
                            helperText={fieldErrors[`extraBirthday_${index}`] || ''} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <FormControl fullWidth sx={{ backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: 1 }}>
                            <InputLabel>Gender *</InputLabel>
                            <Select value={athlete.gender || ''} label="Gender *"
                              onChange={e => updateAdditionalAthlete(index, 'gender', e.target.value)}>
                              <MenuItem value="Female">Female</MenuItem>
                              <MenuItem value="Male">Male</MenuItem>
                              <MenuItem value="Non-binary">Non-binary</MenuItem>
                              <MenuItem value="Prefer not to say">Prefer not to say</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <TextField fullWidth sx={{ backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: 1 }} label="Athlete Phone"
                            value={athlete.phone || ''} onChange={e => updateAdditionalAthlete(index, 'phone', formatPhone(e.target.value))}
                            helperText="Used for event updates only" />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <Autocomplete
                            options={schools} value={athlete.school || null} freeSolo
                            onChange={(_, v) => updateAdditionalAthlete(index, 'school', v || '')}
                            renderInput={(params) => (
                              <TextField fullWidth sx={{ backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: 1 }} {...params} label="School Name *"
                                onChange={e => updateAdditionalAthlete(index, 'school', e.target.value)} />
                            )}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <FormControl fullWidth sx={{ backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: 1 }}>
                            <InputLabel>Current Grade *</InputLabel>
                            <Select value={athlete.grade} label="Current Grade *"
                              onChange={e => updateAdditionalAthlete(index, 'grade', e.target.value)}>
                              {[...Array(8)].map((_, i) => (
                                <MenuItem key={i + 5} value={i + 5}>{`Grade ${i + 5}`}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <FormControl fullWidth sx={{ backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: 1 }}>
                            <InputLabel>Shirt Size *</InputLabel>
                            <Select value={athlete.shirtSize} label="Shirt Size *"
                              onChange={e => updateAdditionalAthlete(index, 'shirtSize', e.target.value)}>
                              {shirtSizes.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <FormControl fullWidth sx={{ backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: 1 }}>
                            <InputLabel>Indoor Experience *</InputLabel>
                            <Select value={athlete.indoorExperience || ''} label="Indoor Experience *"
                              onChange={e => updateAdditionalAthlete(index, 'indoorExperience', e.target.value)}>
                              {experienceLevels.map(l => <MenuItem key={l} value={l}>{l}</MenuItem>)}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <FormControl fullWidth sx={{ backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: 1 }}>
                            <InputLabel>Beach Experience / Class Level *</InputLabel>
                            <Select value={athlete.beachExperience || ''} label="Beach Experience / Class Level *"
                              onChange={e => updateAdditionalAthlete(index, 'beachExperience', e.target.value)}>
                              {beachLevels.map(l => <MenuItem key={l} value={l}>{l}</MenuItem>)}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                          <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 'bold', color: '#444' }}>Training Goals *</Typography>
                          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
                            {goalOptions.map(goal => (
                              <FormControlLabel key={goal}
                                control={<Checkbox checked={(athlete.goals || []).includes(goal)}
                                  onChange={() => {
                                    const current = athlete.goals || [];
                                    updateAdditionalAthlete(index, 'goals', current.includes(goal) ? current.filter(g => g !== goal) : [...current, goal]);
                                  }} />}
                                label={goal} sx={{ display: 'block' }} />
                            ))}
                          </Paper>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField fullWidth sx={{ backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: 1 }} label="Allergies *"
                            value={athlete.allergies || ''} onChange={e => updateAdditionalAthlete(index, 'allergies', e.target.value)} placeholder="None" />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField fullWidth sx={{ backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: 1 }} label="Medical Conditions *"
                            value={athlete.medicalConditions || ''} onChange={e => updateAdditionalAthlete(index, 'medicalConditions', e.target.value)} placeholder="None" />
                        </Grid>
                      </Grid>
                    </Box>
                  ))}
                  <Button variant="contained"
                    onClick={() => setAdditionalAthletes(prev => [...prev, { ...emptyAdditionalAthlete }])}
                    sx={{ mt: 1, backgroundColor: '#1E3A5F', color: 'white', '&:hover': { backgroundColor: '#2E6DA4' } }}>
                    + Add Another Child
                  </Button>
                </Box>
              )}
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 3, color: 'white', fontWeight: 'bold', fontFamily: '"Bebas Neue", sans-serif', letterSpacing: '2px', fontSize: '1.2rem' }}>
              Waivers & Agreements
            </Typography>
            <Paper sx={{ p: 3, mb: 3, backgroundColor: 'rgba(255,255,255,0.06)', borderRadius: 2, border: '1px solid rgba(201,168,76,0.12)', maxHeight: 240, overflowY: 'auto' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#C9A84C' }}>Liability & Media Release Agreement</Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', mb: 2 }}>
                <strong>Hold Harmless Agreement:</strong> I, the undersigned, hereby release and hold harmless Torch Beach Volleyball LLC, its owners, coaches, staff, and volunteers from any and all liability, claims, demands, actions, or causes of action arising out of or related to any loss, damage, or injury that may be sustained by the participant while participating in any activity associated with Torch Beach Volleyball. I acknowledge that volleyball involves inherent risks of injury and I voluntarily assume those risks on behalf of myself and/or my minor child. I further agree to indemnify and hold harmless Torch Beach Volleyball LLC from any loss, liability, damage, or costs that may occur as a result of my or my child's participation.
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)' }}>
                <strong>Media Release:</strong> I hereby grant Torch Beach Volleyball LLC permission to use photographs, video recordings, and other media of myself and/or my minor child taken during volleyball activities for promotional and educational purposes, including social media, website, and marketing materials. No compensation will be provided for such use.
              </Typography>
            </Paper>
            <FormControlLabel
              control={<Checkbox checked={form.holdHarmlessWaiver} onChange={e => { update('holdHarmlessWaiver', e.target.checked); update('mediaWaiver', e.target.checked); }} sx={{ color: 'rgba(255,255,255,0.7)', '&.Mui-checked': { color: '#C9A84C' } }} />}
              label={<Typography variant="body2" sx={{ fontWeight: 600, color: 'white' }}>I agree to the Liability & Media Release Agreement *</Typography>}
              sx={{ mb: 2, display: 'block' }} />
            {form.accountType === 'renter' && (
              <>
                <Paper sx={{ p: 3, mb: 2, backgroundColor: 'rgba(255,255,255,0.06)', borderRadius: 2, border: '1px solid rgba(201,168,76,0.12)' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#C9A84C' }}>Liability Insurance</Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)' }}>
                    Court renters run their own activities on our courts. You must carry
                    your own general liability insurance covering those activities, and
                    the club may ask to see a certificate of insurance at any time.
                  </Typography>
                </Paper>
                <FormControlLabel
                  control={<Checkbox checked={form.liabilityInsurance}
                    onChange={e => update('liabilityInsurance', e.target.checked)} sx={{ color: 'rgba(255,255,255,0.7)', '&.Mui-checked': { color: '#C9A84C' } }} />}
                  label={<Typography variant="body2" sx={{ fontWeight: 600, color: 'white' }}>
                    I have my own liability insurance *
                  </Typography>}
                  sx={{ mb: 2, display: 'block' }} />
              </>
            )}
            <Paper sx={{ p: 3, mb: 3, backgroundColor: 'rgba(255,255,255,0.06)', borderRadius: 2, border: '1px solid rgba(201,168,76,0.12)' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#C9A84C' }}>SMS Notifications (Optional)</Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)' }}>
                Torch Beach Volleyball LLC may send you automated text messages regarding session balance alerts, schedule updates, and club announcements. Message and data rates may apply. You can opt out at any time by replying STOP. View our{' '}
                <a href="/sms-terms" target="_blank" rel="noopener noreferrer" style={{ color: '#C9A84C' }}>SMS Terms & Conditions</a>.
              </Typography>
            </Paper>
            <FormControlLabel
              control={<Checkbox checked={form.smsConsent} onChange={e => update('smsConsent', e.target.checked)} sx={{ color: 'rgba(255,255,255,0.7)', '&.Mui-checked': { color: '#C9A84C' } }} />}
              label={
                <Typography variant="body2">
                  I consent to receive SMS text messages from Torch Beach Volleyball (optional). See{' '}
                  <a href="/sms-terms" target="_blank" rel="noopener noreferrer" style={{ color: '#C9A84C' }}>SMS Terms</a>.
                </Typography>
              }
              sx={{ display: 'block' }} />
          </Box>
        );

      default:
        return null;
    }
  };

  if (autoRegistering) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0D1E35' }}>
        <Box sx={{ textAlign: 'center', color: 'white' }}>
          <CircularProgress sx={{ color: 'white', mb: 2 }} />
          <Typography>Setting up your account...</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{
      minHeight: '100vh', background: '#0D1E35', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 3, pt: 5,
      '& .MuiTextField-root, & .MuiFormControl-root': {
        '& .MuiOutlinedInput-root': {
          color: 'white',
          '& fieldset': { borderColor: 'rgba(201,168,76,0.3)' },
          '&:hover fieldset': { borderColor: 'rgba(201,168,76,0.6)' },
          '&.Mui-focused fieldset': { borderColor: '#C9A84C' },
          '& input, & textarea': { color: 'white' },
          '& .MuiSelect-select': { color: 'white' },
          '& .MuiSvgIcon-root': { color: 'rgba(255,255,255,0.5)' },
        },
        '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' },
        '& .MuiInputLabel-root.Mui-focused': { color: '#C9A84C' },
        '& .MuiFormHelperText-root': { color: 'rgba(255,100,100,0.9)' },
      },
    }}>
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Barlow:wght@300;400;600&display=swap" />
      <Container maxWidth="sm">
        {previewMode && (
          <Alert severity="warning" icon={false}
                 sx={{
                   mb: 3, borderRadius: 2,
                   bgcolor: '#C9A84C', color: '#0D1E35',
                   border: '2px solid #fff',
                   '& .MuiAlert-message': { width: '100%' },
                 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, letterSpacing: 1 }}>
                  🔍 PREVIEW MODE
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', color: '#0D1E35' }}>
                  No data will be saved. Walk through the flow to review UX.
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" variant="outlined"
                        onClick={() => {
                          // Pre-fill with realistic test data so admin doesn't have to type
                          setForm(p => ({
                            ...p,
                            accountType: 'junior',
                            firstName: 'Test', lastName: 'Parent',
                            phone: '(555) 123-4567', nickname: '',
                            relationship: 'Mom',
                            referredBySource: 'Friend', referredByName: 'Jane Doe',
                            address: '123 Beach Rd', city: 'Geneva', state: 'FL', zip: '32732',
                            guardian2FirstName: '', guardian2LastName: '',
                            guardian2Phone: '', guardian2Email: '',
                            emergencyContactName: 'Backup Person',
                            emergencyContactPhone: '(555) 987-6543',
                            emergencyContactRelationship: 'Friend',
                            athleteFirstName: 'Test', athleteLastName: 'Athlete',
                            athleteNickname: '', athletePhone: '', athleteGender: 'Female',
                            athleteBirthday: '2010-05-15',
                            athleteSchool: 'Test Middle School',
                            athleteGrade: '8',
                            athleteShirtSize: 'M',
                            indoorExperience: 'Beginner',
                            beachExperience: 'Beginner',
                            goals: ['Skill development'],
                            allergies: 'None', medicalConditions: 'None',
                            holdHarmlessWaiver: true, mediaWaiver: true, smsConsent: true,
                          }));
                          setActiveStep(3); // jump to last step (waivers)
                        }}
                        sx={{ borderColor: '#0D1E35', color: '#0D1E35', fontWeight: 600 }}>
                  Fill Test Data
                </Button>
                <Button size="small" variant="outlined"
                        onClick={() => navigate('/day-pass-checkout?preview=1')}
                        sx={{ borderColor: '#0D1E35', color: '#0D1E35', fontWeight: 600 }}>
                  Preview Mini-Reg
                </Button>
                <Button size="small" variant="contained"
                        onClick={() => navigate('/tournament-admin')}
                        sx={{ bgcolor: '#0D1E35', color: '#fff', '&:hover': { bgcolor: '#1a3a5c' } }}>
                  Exit
                </Button>
              </Box>
            </Box>
          </Alert>
        )}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <img src="/horzlogo.png" alt="Torch Beach Volleyball" style={{ maxWidth: 240, width: '60vw', objectFit: 'contain' }} />
          <Typography variant="h5" sx={{ fontFamily: '"Bebas Neue", sans-serif', letterSpacing: '3px', color: 'white', mt: 2, fontSize: '1.8rem' }}>Create Your Account</Typography>
          <Typography variant="body2" sx={{ color: 'rgba(201,168,76,0.6)', letterSpacing: '1.5px', textTransform: 'uppercase', fontSize: '0.72rem' }}>Torch Beach Volleyball — Member Registration</Typography>
        </Box>
        <Paper elevation={8} sx={{ borderRadius: 3, overflow: 'hidden', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(201,168,76,0.2)' }}>
          <Box sx={{ p: 3, backgroundColor: 'rgba(0,0,0,0.2)', borderBottom: '1px solid rgba(201,168,76,0.15)' }}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map(label => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
            </Stepper>
          </Box>
          <Box sx={{ p: 3, color: 'white' }}>
            {coParentMode && (
              <Alert severity="success" sx={{ mb: 3, backgroundColor: 'rgba(39,174,96,0.15)', color: '#C9A84C', border: '1px solid rgba(201,168,76,0.3)' }}>
                <strong>Co-parent registration for the {coParentFamilyName.split(' ').pop() || ''} family.</strong>
                {' '}You've already been linked to the existing athletes. Confirm your details and submit to finish.
              </Alert>
            )}
            {memberShell && !coParentMode && (
              <Alert severity="success" sx={{ mb: 3, backgroundColor: 'rgba(39,174,96,0.15)', color: '#C9A84C', border: '1px solid rgba(201,168,76,0.3)' }}>
                <strong>Welcome back{memberShell.firstName ? `, ${memberShell.firstName}` : ''}! We found your {memberShell.membership.tierName || 'membership'}.</strong>
                {' '}Finish this one-time registration and it will connect to this login, along with your gate access and payment history.
              </Alert>
            )}
            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
            {notice && <Alert severity="info" sx={{ mb: 3 }}>{notice}</Alert>}
            {dupCandidate && !linkSentTo && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                We found an existing account that matches your name and phone number — possibly under a different email. Is this you?
                {/* Says where the code goes before they press it. Somebody who
                    is not who they say they are should be able to see that the
                    proof lands somewhere they cannot reach. */}
                <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                  We will email a code to the address already on that account, and it has to come back here before anything is linked.
                </Typography>
                {linkError && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#B3261E', fontWeight: 600 }}>
                    {linkError}
                  </Typography>
                )}
                <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button size="small" variant="contained" onClick={() => handleSubmit('link')} disabled={saving || linkBusy}
                    sx={{ backgroundColor: '#C9A84C', color: '#0D1E35', fontWeight: 700 }}>
                    {linkBusy ? 'Sending…' : 'Yes — email me the code'}
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => handleSubmit('new')} disabled={saving || linkBusy}>
                    No, create a new account
                  </Button>
                </Box>
              </Alert>
            )}

            {dupCandidate && linkSentTo && (
              <Alert severity="info" sx={{ mb: 3 }}>
                We sent a six-digit code to <strong>{linkSentTo}</strong>. Enter it here to join this sign-in to that account.
                {linkError && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#B3261E', fontWeight: 600 }}>
                    {linkError}
                  </Typography>
                )}
                <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                  <TextField size="small" label="Code" value={linkCode} disabled={linkBusy}
                    onChange={e => setLinkCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    inputProps={{ inputMode: 'numeric', maxLength: 6, style: { letterSpacing: '0.4em', fontFamily: 'monospace' } }}
                    sx={{ width: 140, backgroundColor: 'white', borderRadius: 1 }} />
                  <Button size="small" variant="contained" onClick={submitLinkCode} disabled={linkBusy}
                    sx={{ backgroundColor: '#C9A84C', color: '#0D1E35', fontWeight: 700 }}>
                    {linkBusy ? 'Checking…' : 'Link my account'}
                  </Button>
                  <Button size="small" variant="text" disabled={linkBusy}
                    onClick={() => { setLinkSentTo(null); setLinkCode(''); setLinkError(''); }}>
                    Back
                  </Button>
                </Box>
                <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                  It expires in fifteen minutes. If you cannot reach that inbox, choose "create a new account" instead and the club can merge them for you.
                </Typography>
              </Alert>
            )}
            {renderStep()}
          </Box>
          <Box sx={{ p: 3, backgroundColor: 'rgba(0,0,0,0.2)', borderTop: '1px solid rgba(201,168,76,0.15)', display: 'flex', justifyContent: 'space-between' }}>
            <Button onClick={handleBack} disabled={activeStep === 0} variant="outlined" sx={{ px: 4, borderColor: 'rgba(201,168,76,0.4)', color: 'rgba(201,168,76,0.7)', '&:hover': { borderColor: '#C9A84C', color: '#C9A84C' } }}>Back</Button>
            {activeStep < steps.length - 1 ? (
              activeStep === 0 ? null :
              <Button onClick={handleNext} variant="contained" sx={{ px: 4, backgroundColor: '#C9A84C', color: '#0D1E35', fontWeight: 700, '&:hover': { backgroundColor: '#e0be6a' } }}>Next</Button>
            ) : (
              <Button onClick={() => handleSubmit()} variant="contained" disabled={saving}
                sx={{ px: 4, backgroundColor: '#C9A84C', color: '#0D1E35', fontWeight: 700, '&:hover': { backgroundColor: '#e0be6a' } }}>
                {saving ? 'Saving...' : 'Complete Registration'}
              </Button>
            )}
          </Box>
          <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'rgba(0,0,0,0.2)' }}>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)' }}>
              Already have an account?{' '}
              {/* Hard navigation, not navigate(): the /login route redirects a
                  still-logged-in unregistered user back to /register, and the
                  auth-state change from signOut() may not have reached React
                  yet. A reload re-reads auth from persistence — signed out —
                  so the sign-in form is what actually renders. */}
              <span onClick={async () => { await signOut(auth); window.location.assign('/login'); }}
                style={{ color: '#C9A84C', fontWeight: 600, cursor: 'pointer' }}>
                Sign in here
              </span>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

export default Register;
